import React, { useEffect, useState } from 'react';
import { signOut } from 'aws-amplify/auth';
import { getCurrentUser } from '../services/app.service';
import Loading from '../components/Loading';
import { App as AntdApp, message as staticMessage } from 'antd';
import type { MessageInstance } from 'antd/es/message/interface';

// Global message instance that will be set by AppProvider
let globalMessage: MessageInstance = staticMessage;

// Export a message object that proxies to the global instance
export const message = {
    success: (...args: Parameters<MessageInstance['success']>) => globalMessage.success(...args),
    error: (...args: Parameters<MessageInstance['error']>) => globalMessage.error(...args),
    warning: (...args: Parameters<MessageInstance['warning']>) => globalMessage.warning(...args),
    info: (...args: Parameters<MessageInstance['info']>) => globalMessage.info(...args),
    loading: (...args: Parameters<MessageInstance['loading']>) => globalMessage.loading(...args),
    open: (...args: Parameters<MessageInstance['open']>) => globalMessage.open(...args),
    destroy: (...args: Parameters<MessageInstance['destroy']>) => globalMessage.destroy(...args),
};

const App = React.createContext<{
    user?: any;
    setUser?: any;
    logout?: () => void;
    setLoading?: any;
    activity?: any;
    setActivity?: any;
    coords?: any;
    setCoords?: any;
    importPayload?: any;
    setImportPayload?: any;
    message?: MessageInstance;
}>({});

export const AppProvider = ({ children }: any) => {
    const [user, setUser] = useState<any>(null);
    const [ready, setReady] = useState(false);
    const [loading, setLoading] = useState(true);
    const [activity, setActivity] = useState<any>();
    const [coords, setCoords] = useState<Array<any>>([]);
    const [importPayload, setImportPayload] = useState<any>();

    // Get message instance from Ant Design's App context
    const { message: antdMessage } = AntdApp.useApp();

    // Set the global message instance when the component mounts
    useEffect(() => {
        globalMessage = antdMessage;
    }, [antdMessage]);

    useEffect(() => {
        const run = async () => {
            const currentUser = await getCurrentUser().catch((reason) => null);
            setUser(currentUser);
            setLoading(false);
        };
        run().finally(() => {
            setReady(true);
        });
    }, []);

    const logout = () => {
        setUser(null);
        signOut().then();
    };

    return (
        <App.Provider
            value={{
                user,
                setUser,
                logout,
                setLoading,
                activity,
                setActivity,
                coords,
                setCoords,
                importPayload,
                setImportPayload,
                message: antdMessage,
            }}
        >
            {ready ? children : null}
            {loading ? <Loading /> : null}
        </App.Provider>
    );
};

export const useApp = () => React.useContext(App);
